import React from 'react';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import * as Yup from 'yup';
import { isAfter, isValid, parseISO } from 'date-fns';
import { WidgetsKindEnum } from 'graphql-common';
import { FieldItem, Values } from '@lib/interfaces/form';
import { SelectOption } from '@lib/components/Select/types';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageIconColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import WidgetFieldNames from '@lib/enums/fieldNames/widgetFieldNames';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsChipColor from '@lib/components/RadioButtonAsChipColor/RadioButtonAsChipColor';
import getChipTypesOptions from '@lib/components/Chip/utils/getChipTypesOptions';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import DateTimePicker from '@lib/components/DateTimePicker/DateTimePicker';
import { InputTypes } from '@lib/components/Input/enums';
import {
  MAX_TEXT_EDITOR_FIELD_LENGTH,
  MAX_TEXT_FIELD_LENGTH,
} from '@lib/enums/form';
import ImageUploader, {
  ImageUploaderMode,
} from '@lib/components/ImageUploader/ImageUploader';
import {
  FileSizeLimits,
  fileSizeLimit,
  UppyImageAllowedFileTypes,
} from '@lib/enums/mimeTypes';
import Typography from '@lib/components/Typography/Typography';
import formStyles from '@lib/assets/styles/Form.module.scss';
import {
  isWidgetSelected,
  getConditionalSelectValidation,
} from './validationHelpers';

/**
 * Returns fields for Announcement widget
 */
export default function getAnnouncementFields(t: TFunction): FieldItem[] {
  return [
    {
      name: WidgetFieldNames.AnnouncementInfo,
      customRender: () => (
        <SupportiveMessage
          text={t('home.widget-announcement-info')}
          leftIcon="info"
          color={SupportiveMessageColor.Gray}
          iconColor={SupportiveMessageIconColor.Inner}
          type={SupportiveMessageType.Transparent}
          hasBottomSpace={false}
        />
      ),
      isVisibleFn: (values: Values) =>
        isWidgetSelected(values, WidgetsKindEnum.Announcement),
    },
    // Appearance settings section
    {
      name: WidgetFieldNames.AnnouncementAppearanceTitle,
      customRender: () => (
        <Typography variant="h3">{t('appearance-settings')}</Typography>
      ),
      isVisibleFn: (values: Values) =>
        isWidgetSelected(values, WidgetsKindEnum.Announcement),
    },
    {
      name: WidgetFieldNames.AnnouncementBoxColor,
      label: t('announcement-box-color'),
      element: RadioButtonGroup,
      validation: getConditionalSelectValidation(
        t,
        WidgetsKindEnum.Announcement,
      ),
      componentProps: {
        options: getChipTypesOptions({ t }),
        RadioButtonComponent: RadioButtonAsChipColor,
        wrapView: RadioButtonWrapViews.colors,
      },
      isOptionalFn: () => false,
      isVisibleFn: (values: Values) =>
        isWidgetSelected(values, WidgetsKindEnum.Announcement),
    },
    {
      name: WidgetFieldNames.AnnouncementMessageContent,
      label: t('message-content'),
      element: TextEditor,
      validation: Yup.string()
        .max(MAX_TEXT_EDITOR_FIELD_LENGTH, t('max-text-length-field-error'))
        .when(WidgetFieldNames.Widget, {
          is: (widget: SelectOption | undefined) =>
            _get(widget, 'data.kind') === WidgetsKindEnum.Announcement,
          then: (schema) => schema.required(t('required-field-error')),
          otherwise: (schema) => schema.notRequired(),
        }),
      isOptionalFn: () => false,
      isVisibleFn: (values: Values) =>
        isWidgetSelected(values, WidgetsKindEnum.Announcement),
    },
    {
      name: WidgetFieldNames.AnnouncementImage,
      element: ImageUploader,
      componentProps: {
        label: `${t('image')} (${t('optional')})`,
        mode: ImageUploaderMode.Compact,
        showOfflineMessage: true,
        allowedFileTypes: UppyImageAllowedFileTypes,
        note: t('image-upload-validation-msg', {
          size: FileSizeLimits.Image,
        }),
        maxFileSize: fileSizeLimit,
        uploadButtonProps: {
          buttonText: t('upload-image'),
          leftIcon: 'upload',
          fullWidth: true,
        },
        useReuploadButton: true,
        showDeleteAllButton: true,
      },
      isVisibleFn: (values: Values) =>
        isWidgetSelected(values, WidgetsKindEnum.Announcement),
    },
    // Schedule section
    {
      name: WidgetFieldNames.AnnouncementScheduleTitle,
      customRender: () => <Typography variant="h3">{t('schedule')}</Typography>,
      isVisibleFn: (values: Values) =>
        isWidgetSelected(values, WidgetsKindEnum.Announcement),
    },
    {
      name: WidgetFieldNames.AnnouncementStartAt,
      label: t('start-display-on'),
      element: DateTimePicker,
      formItemClassName: formStyles.formColumn,
      componentProps: {
        type: InputTypes.dateTime,
      },
      validation: Yup.string()
        .trim()
        .max(MAX_TEXT_FIELD_LENGTH, t('max-text-length-field-error'))
        .test('isValidDateTime', t('date-time-field-format-error'), (value) => {
          if (!value) return true;
          const date = parseISO(value);
          return isValid(date);
        }),
      isVisibleFn: (values: Values) =>
        isWidgetSelected(values, WidgetsKindEnum.Announcement),
    },
    {
      name: WidgetFieldNames.AnnouncementEndAt,
      label: t('end-display-on'),
      element: DateTimePicker,
      formItemClassName: formStyles.formColumn,
      componentProps: {
        type: InputTypes.dateTime,
      },
      validation: Yup.string()
        .trim()
        .max(MAX_TEXT_FIELD_LENGTH, t('max-text-length-field-error'))
        .test('isValidDateTime', t('date-time-field-format-error'), (value) => {
          if (!value) return true;
          const date = parseISO(value);
          return isValid(date);
        })
        .when(WidgetFieldNames.AnnouncementStartAt, (deps, schema) =>
          _get(deps, 0)
            ? schema.test(
                'isAfterStartAt',
                t('date-time-range-invalid-error'),
                (endAt) => {
                  if (!endAt) return true;
                  return isAfter(parseISO(endAt), parseISO(_get(deps, 0)));
                },
              )
            : schema,
        ),
      isVisibleFn: (values: Values) =>
        isWidgetSelected(values, WidgetsKindEnum.Announcement),
    },
  ];
}
